'use client';
import React, { FC } from 'react';
import Slider, { Settings } from 'react-slick';
import { TypeEmbeddedCarouselFields } from '@/core/generated-types';
import { Arrow } from '../Carousel/Arrows';
import { HeaderText } from '@/components/ui';
import { HeaderSize } from '@/components/ui/HeaderText';
import { Image } from '@/components/ui/Image';
import { UniversalLink } from '../UniversalLink';
import { DynamicComponent } from '../DynamicComponent';
import { RichText } from '../RichText';
import { Document } from '@contentful/rich-text-types';

const ContentCarousel: FC<TypeEmbeddedCarouselFields> = ({
  leadingBackgroundColor,
  trailingBackgroundColor,
  badge,
  title,
  content,
  subtitle,
  cta,
  slides = [],
  trailingText,
}) => {
  const settings: Settings = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <Arrow direction={'right'} />,
    prevArrow: <Arrow direction={'left'} />,
  };

  return (
    <div className="flex flex-col md:flex-row">
      {badge && (
        <div
          className={`flex justify-center md:hidden bg-${leadingBackgroundColor?.fields?.color} pb-4 pt-5`}
        >
          <DynamicComponent component={badge} />
        </div>
      )}
      <div
        className={`w-full flex-none md:w-1/2 bg-${leadingBackgroundColor?.fields?.color} order-2 flex items-center justify-stretch md:order-1 md:justify-end`}
      >
        <div className="px-6 pb-10 pt-12 md:max-w-[650px] md:px-12 md:pb-[110px] md:pt-[90px]">
          {badge && (
            <div className="mb-5 hidden justify-center md:flex md:justify-start">
              <DynamicComponent component={badge} />
            </div>
          )}
          {title && (
            <HeaderText
              size={HeaderSize.H2}
              text={title}
              classes="mb-2 md:text-left text-center"
            />
          )}
          {content && (
            <div className="text-center md:text-left">
              <RichText content={content as Document} />
            </div>
          )}
          {cta && (
            <UniversalLink
              link={cta}
              classes="btn-primary btn btn-responsive md:inline-flex mx-auto md:mx-0"
            />
          )}
          {trailingText && (
            <div className="mt-2 text-center text-sm md:text-left md:text-base">
              {trailingText}
            </div>
          )}
        </div>
      </div>
      <div
        className={`w-full flex-none md:w-1/2 bg-${trailingBackgroundColor?.fields?.color} relative order-1 flex items-center justify-center md:order-2`}
      >
        {subtitle && (
          <div className="absolute top-3 z-10 w-full px-6 text-center text-xl font-bold md:top-[90px] md:px-12">
            {subtitle}
          </div>
        )}
        <div className="h-auto w-full pb-0 pt-12 md:absolute md:h-full md:pb-[62px] md:pt-[90px]">
          <Slider
            {...settings}
            className="slider-variant-embedded-carousel h-full w-full"
          >
            {slides.map((slide) => {
              return (
                <Image
                  alt={slide?.fields?.altText}
                  className="h-full w-full object-contain"
                  imageData={slide}
                  key={slide.sys.id}
                />
              );
            })}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ContentCarousel);
